import { useState, useEffect, useRef, useMemo } from "react";
import { inventoryTransactionApi, inventorySummaryApi, productApi, inventoryItemDtlApi, vendorApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination }       from "../hooks/usePagination";
import Pagination              from "../components/Pagination";
import SearchableDropdown      from "../components/SearchableDropdown";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";

/* ── Movement Classification (Strictly Inward vs Outward) ── */
const getMovementKind = (tx) => {
  const type = String(tx?.transactionType || "").toLowerCase();
  const isOutward = type.includes("out") || type.includes("sale") || Number(tx?.quantity) < 0;
  return isOutward ? "out" : "in";
};

const MOVEMENT_CONFIG = {
  in: {
    key: "in",
    label: "Delivery Inward",
    pillClass: "rv-txn-type-pill--in",
    qtyClass: "rv-txn-qty--in",
    sign: "+",
    iconType: "in",
  },
  out: {
    key: "out",
    label: "Sale Outward",
    pillClass: "rv-txn-type-pill--out",
    qtyClass: "rv-txn-qty--out",
    sign: "−",
    iconType: "out",
  },
};

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Transaction Detail Drawer (Read-Only Audit View) ── */
function TransactionDrawer({ txn, getProductName, getVendor, getItemLabel, onNavigate, onClose }) {
  const kind = getMovementKind(txn);
  const conf = MOVEMENT_CONFIG[kind];
  const absQty = Math.abs(Number(txn.quantity) || 0);

  const handleNavigateToSource = () => {
    const targetPage = conf.key === "in" ? "Stock Receipt" : "Invoice";
    if (onNavigate) {
      onNavigate(targetPage);
    } else {
      window.dispatchEvent(new CustomEvent("reva:navigate", { detail: targetPage }));
    }
    onClose();
  };

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-txn-drawer" onClick={e => e.stopPropagation()}>
        <div className="rv-txn-drawer-header">
          <div className={`rv-txn-drawer-avatar rv-txn-avatar--${conf.key}`}>
            <TxnIcon type={conf.iconType} />
          </div>
          <div className="rv-txn-drawer-title-wrap">
            <h3 className="rv-txn-drawer-name">Movement #{txn.transactionID}</h3>
            <span className={`rv-txn-type-pill ${conf.pillClass}`}>
              {conf.label}
            </span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-txn-drawer-body">
          <div className="rv-txn-detail-grid">
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><ProductIcon /> Product</span>
              <span className="rv-txn-detail-val">{getProductName(txn.productID)}</span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><TruckIcon /> Vendor / Customer Source</span>
              <span className="rv-txn-detail-val">
                {(() => {
                  const v = getVendor ? getVendor(txn.productID) : null;
                  if (!v) return <span className="rv-empty-val">—</span>;
                  return (
                    <span className="rv-txn-vendor-chip">
                      <strong>{v.vendorName}</strong>
                      {(v.phoneNumber || v.contactNumber) ? (
                        <span className="rv-txn-vendor-sub">{v.phoneNumber || v.contactNumber}</span>
                      ) : null}
                    </span>
                  );
                })()}
              </span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><QtyIcon /> Quantity Movement</span>
              <span className={`rv-txn-detail-val rv-txn-detail-qty ${conf.qtyClass}`}>
                {conf.sign}{absQty} pcs
              </span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><ItemIcon /> Tagged Piece</span>
              <span className="rv-txn-detail-val">{getItemLabel(txn.inventoryItemID)}</span>
            </div>
            <div className="rv-txn-detail-item rv-txn-detail-item--full">
              <span className="rv-txn-detail-label"><CommentIcon /> Reference &amp; Audit Trail</span>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                background: "var(--cream-light, #fbf9f6)",
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid rgba(160, 120, 80, 0.16)",
                marginTop: 4,
              }}>
                <span className="rv-txn-detail-comment" style={{ fontSize: 13, color: "var(--burgundy-dark, #2a151b)", fontWeight: 500 }}>
                  {txn.comments || (conf.key === "in" ? "Vendor delivery recorded in Deliveries" : "Customer sale finalised in Invoice")}
                </span>
                <button
                  type="button"
                  className="rv-btn rv-btn--primary"
                  style={{
                    padding: "6px 14px",
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 6,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    whiteSpace: "nowrap"
                  }}
                  onClick={handleNavigateToSource}
                  title={conf.key === "in" ? "Go to Deliveries page" : "Go to Invoices page"}
                >
                  <span>{conf.key === "in" ? "Go to Deliveries" : "Go to Invoices"}</span>
                  <span style={{ fontSize: 13 }}>→</span>
                </button>
              </div>
            </div>
            {txn.lastUPDTTM && (
              <div className="rv-txn-detail-item rv-txn-detail-item--full">
                <span className="rv-txn-detail-label"><ClockIcon /> Recorded Date &amp; Time</span>
                <span className="rv-txn-detail-val">
                  {new Date(txn.lastUPDTTM).toLocaleString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="rv-txn-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   STOCK MOVEMENT HISTORY (READ-ONLY AUDIT TRAIL)
══════════════════════════════════════ */
export default function InventoryTransactionPage({ onNavigate }) {
  const [transactions,   setTransactions]   = useState([]);
  const [products,       setProducts]       = useState([]);
  const [vendors,        setVendors]        = useState([]);
  const [loading,        setLoading]        = useState(true);

  const [search,       setSearch]       = useState("");
  const [filterType,   setFilterType]   = useState("all");
  const [filterProd,   setFilterProd]   = useState("all");
  const [filterVendor, setFilterVendor] = useState("all");

  const [drawer,  setDrawer]  = useState(null);
  const [toast,   setToast]   = useState(null);

  /* ── Resizable columns (9 columns) ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 160, 300, 120, 190, 120]);

  /* ── Fetch all ── */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const [txnData, prodData, vendorData] = await Promise.all([
        inventoryTransactionApi.getAll(),
        productApi.getAll(),
        vendorApi.getAll(),
      ]);
      setTransactions( Array.isArray(txnData)    ? txnData    : txnData?.data    ?? []);
      setProducts(     Array.isArray(prodData)   ? prodData   : prodData?.data   ?? []);
      setVendors(      Array.isArray(vendorData) ? vendorData : vendorData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load stock movements.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Lookup helpers ── */
  const getProductName = id => products.find(p => p.productID === id)?.productName ?? (id ? `Prod #${id}` : "—");
  const getVendor      = prodId => {
    const prod = products.find(p => p.productID === prodId);
    if (!prod || !prod.vendorID) return null;
    return vendors.find(v => v.vendorID === prod.vendorID) ?? null;
  };
  const getVendorName  = prodId => getVendor(prodId)?.vendorName ?? "—";
  const getItemLabel   = id => id ? `Item #${id}` : "—";

  /* ── Pure Inward vs Outward Stats ── */
  const statCounts = useMemo(() => {
    let inCount = 0;
    let outCount = 0;
    transactions.forEach(tx => {
      if (getMovementKind(tx) === "in") inCount++;
      else outCount++;
    });
    return { in: inCount, out: outCount };
  }, [transactions]);

  /* ── Product & Vendor options for filter ── */
  const prodOptions = Array.from(
    new Map(transactions.map(tx => [tx.productID, getProductName(tx.productID)])).entries()
  );
  const vendorOptions = vendors.filter(v => v && v.vendorID);

  /* ── Filtered list ── */
  const filtered = transactions.filter(tx => {
    const q = search.toLowerCase();
    const vName = getVendorName(tx.productID).toLowerCase();
    const kind = getMovementKind(tx);
    const kindLabel = kind === "in" ? "delivery inward" : "sale outward";

    const matchSearch =
      !q ||
      getProductName(tx.productID).toLowerCase().includes(q) ||
      vName.includes(q) ||
      kindLabel.includes(q) ||
      tx.comments?.toLowerCase().includes(q) ||
      String(tx.transactionID).includes(q) ||
      String(tx.inventoryItemID).includes(q);

    const matchType   = filterType === "all" || kind === filterType;
    const matchProd   = filterProd === "all" || tx.productID === Number(filterProd);
    const matchVendor = filterVendor === "all" || getVendor(tx.productID)?.vendorID === Number(filterVendor);
    return matchSearch && matchType && matchProd && matchVendor;
  });

  /* ── Pagination ── */
  const {
    paged, page, totalPages, pageSize,
    setPage, setPageSize,
    pageStart, pageEnd, totalItems,
  } = usePagination(filtered, 10);

  /* ── Format date ── */
  const fmtDate = iso => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="rv-txn-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {drawer && (
        <TransactionDrawer
          txn={drawer}
          getProductName={getProductName}
          getVendor={getVendor}
          getItemLabel={getItemLabel}
          onNavigate={onNavigate}
          onClose={() => setDrawer(null)}
        />
      )}

      {/* ── Topbar (Read-Only Audit Trail) ── */}
      <div className="rv-txn-topbar">
        <div className="rv-txn-topbar-left" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <h2 className="rv-txn-heading">Stock Movement History</h2>
            <span className="rv-txn-count">{transactions.length} movements logged</span>
          </div>
          <p style={{ margin: 0, fontSize: "13px", color: "var(--taupe, #8a7a6b)" }}>
            Tamper-proof audit trail of stock received via <strong>Deliveries</strong> and deducted on finalised sales <strong>Invoices</strong>.
          </p>
        </div>
      </div>

      {/* ── Stat pills (Strictly 2 cards: Inward Deliveries vs Sales Outward) ── */}
      {!loading && transactions.length > 0 && (
        <div className="rv-txn-stats" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
          <div className="rv-txn-stat-pill" style={{ background: "#FFFFFF", padding: "14px 18px", borderRadius: 10, border: "1px solid rgba(160,120,80,.16)" }}>
            <div className="rv-txn-stat-icon rv-txn-stat-icon--in">
              <TxnIcon type="in" />
            </div>
            <div>
              <p className="rv-txn-stat-label">Deliveries (Inward)</p>
              <p className="rv-txn-stat-val" style={{ color: "#27ae60" }}>{statCounts.in}</p>
            </div>
          </div>

          <div className="rv-txn-stat-pill" style={{ background: "#FFFFFF", padding: "14px 18px", borderRadius: 10, border: "1px solid rgba(160,120,80,.16)" }}>
            <div className="rv-txn-stat-icon rv-txn-stat-icon--out">
              <TxnIcon type="out" />
            </div>
            <div>
              <p className="rv-txn-stat-label">Sale Outward (From Sales Invoices)</p>
              <p className="rv-txn-stat-val" style={{ color: "#a07850" }}>{statCounts.out}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="rv-txn-filters" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr auto", gap: 10, alignItems: "center" }}>
        <div className="rv-txn-search">
          <SearchIcon />
          <input
            placeholder="Search by product, reference, receipt/invoice #…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-txn-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <SearchableDropdown
          options={[
            { id: "all", name: "All Products" },
            ...prodOptions.map(([id, name]) => ({ id, name }))
          ]}
          valueKey="id"
          labelKey="name"
          value={filterProd}
          onChange={val => { setFilterProd(val); setPage(1); }}
          placeholder="All Products"
          searchPlaceholder="Search products…"
          className="rv-txn-prod-filter"
          pageSize={10}
        />

        <SearchableDropdown
          options={[
            { id: "all", name: "All Vendors" },
            ...vendorOptions.map(v => ({ id: v.vendorID, name: v.vendorName }))
          ]}
          valueKey="id"
          labelKey="name"
          value={filterVendor}
          onChange={val => { setFilterVendor(val); setPage(1); }}
          placeholder="All Vendors"
          searchPlaceholder="Search vendors…"
          className="rv-txn-vendor-filter"
          pageSize={10}
        />

        <div className="rv-filter-tabs">
          <button
            className={`rv-filter-tab ${filterType === "all" ? "rv-filter-tab--active" : ""}`}
            onClick={() => { setFilterType("all"); setPage(1); }}
          >
            All ({transactions.length})
          </button>
          <button
            className={`rv-filter-tab rv-filter-tab--txn ${filterType === "in" ? "rv-filter-tab--active" : ""}`}
            onClick={() => { setFilterType("in"); setPage(1); }}
          >
            Delivery Inward ({statCounts.in})
          </button>
          <button
            className={`rv-filter-tab rv-filter-tab--txn ${filterType === "out" ? "rv-filter-tab--active" : ""}`}
            onClick={() => { setFilterType("out"); setPage(1); }}
          >
            Sale Outward ({statCounts.out})
          </button>
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-txn-card">
        {/* Desktop Table View */}
        <div className="rv-desktop-table-view">
          {loading ? (
            <div className="rv-txn-empty">
              <div className="rv-loader" />
              <p>Loading stock movements…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rv-txn-empty">
              <div className="rv-empty-icon"><TxnIcon type="in" /></div>
              <p className="rv-empty-title">No stock movements found</p>
              <p className="rv-empty-sub">
                {search
                  ? "Try adjusting your search terms or filters."
                  : "Stock movements are recorded automatically when vendor deliveries arrive in Deliveries or customer sales are finalised in Invoice."}
              </p>
            </div>
          ) : (
            <div className="rv-table-wrap rv-txn-table-wrap">
              <table className="rv-table rv-txn-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Movement Type</th>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Date &amp; Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((tx, i) => {
                    const kind = getMovementKind(tx);
                    const conf = MOVEMENT_CONFIG[kind];
                    const absQty = Math.abs(Number(tx.quantity) || 0);

                    return (
                      <tr
                        key={tx.transactionID}
                        className="rv-txn-row"
                        onClick={() => setDrawer(tx)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="rv-txn-idx">{pageStart + i}</td>

                        {/* Movement Type */}
                        <td>
                          <span className={`rv-txn-type-pill ${conf.pillClass}`}>
                            {conf.label}
                          </span>
                        </td>

                        {/* Product */}
                        <td>
                          <div className="rv-txn-prod-cell">
                            <span className="rv-txn-prod-name">{getProductName(tx.productID)}</span>
                          </div>
                        </td>

                        {/* Qty */}
                        <td>
                          <span className={`rv-txn-qty ${conf.qtyClass}`}>
                            {conf.sign}{absQty} pcs
                          </span>
                        </td>

                        {/* Date */}
                        <td>
                          <span className="rv-txn-date">{fmtDate(tx.lastUPDTTM)}</span>
                        </td>

                        {/* Actions */}
                        <td onClick={e => e.stopPropagation()}>
                          <div className="rv-actions">
                            <button
                              type="button"
                              className="rv-btn rv-btn--ghost"
                              style={{ padding: "4px 10px", fontSize: 11.5 }}
                              title="View movement audit details"
                              onClick={() => setDrawer(tx)}
                            >
                              View Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Cards View */}
        <div className="rv-mobile-cards-view">
          <RvDataCardList
            loading={loading}
            items={paged}
            emptyIcon={<TxnIcon type="in" />}
            emptyTitle="No stock movements found"
            emptySubtitle={search
              ? "Try adjusting your search terms or filters."
              : "Stock movements are recorded automatically when vendor deliveries arrive in Stock Inward or customer sales are finalised in Invoice."}
            renderCard={(tx, i) => {
              const kind = getMovementKind(tx);
              const conf = MOVEMENT_CONFIG[kind];
              const absQty = Math.abs(Number(tx.quantity) || 0);
              const qtyDisplay = `${conf.sign}${absQty} pcs`;

              return (
                <RvDataCard
                  key={tx.transactionID}
                  index={pageStart + i}
                  title={getProductName(tx.productID)}
                  subtitle={`Movement #${tx.transactionID} • Qty: ${qtyDisplay}`}
                  badge={
                    <span className={`rv-txn-type-pill ${conf.pillClass}`}>
                      {conf.label}
                    </span>
                  }
                  fields={[
                    { label: "Quantity", value: qtyDisplay },
                    { label: "Date & Time", value: fmtDate(tx.lastUPDTTM) },
                  ]}
                  onClick={() => setDrawer(tx)}
                  actions={
                    <div className="rv-actions" onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        className="rv-btn rv-btn--ghost"
                        style={{ padding: "4px 12px", fontSize: 12 }}
                        onClick={() => setDrawer(tx)}
                      >
                        View Details
                      </button>
                    </div>
                  }
                />
              );
            }}
          />
        </div>

        {/* Shared Pagination for Desktop & Mobile */}
        {!loading && filtered.length > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
            pageStart={pageStart}
            pageEnd={pageEnd}
            totalItems={totalItems}
          />
        )}
      </div>

      {/* ── Resize hint ── */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view">
          <GripIcon /> Drag column borders to resize · Click any row to view full audit details
        </p>
      )}
    </div>
  );
}

/* ── TxnIcon — changes per type ── */
function TxnIcon({ type }) {
  if (type === "in")  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>;
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>;
}

function SearchIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function CloseIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function GripIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{width:13,height:13}}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }
function ProductIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function QtyIcon()     { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>; }
function ItemIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function CommentIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>; }
function ClockIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>; }
function TruckIcon({ size = 14 }) { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width={size} height={size} style={{ width: size, height: size, flexShrink: 0 }}><rect x="1" y="3" width="15" height="13" rx="1"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>; }
